import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { observer } from "mobx-react";
import { Utils } from "Utils/utils";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { TabNavigation } from "../common/TabNavigation";
import LeadsTab from "./Leads";
import ContactsTab from "./Contacts";
import OwnersTab from "../common/Owners";
import ActivityTab from "./ActivityTab";
import { zohoDataStore } from "./ZohoStoreData";
import SyncLogs from "./SyncLogs";
import NewTooltip from "Components/common/NewTooltip";
import { settingsStoreData } from "Stores/settingsData";

const ZohoTabFields = observer(props => {
  const {
    className = "",
    crmSettings = {},
    constructZohoConfig = () => {}
  } = props;
  const [activeTab, setActiveTab] = useState("activity");
  const [previousTab, setPreviousTab] = useState(activeTab);

  let location = useLocation(),
    history = useHistory(),
    hashValue = location?.hash;

  let hashUrlArr = ["#activity", "#leads", "#contacts", "#owners", "#Logs"];

  const tabList = [
    { value: "Activity", id: "activity", path: "activity", active: true },
    {
      value: "Leads",
      id: "leads",
      path: "leads"
    },
    {
      value: "Contacts",
      id: "contacts",
      path: "contacts"
    },
    {
      value: "Owners",
      id: "owners",
      path: "owners"
    },
    {
      value: "Logs",
      id: "logs",
      path: "logs"
    }
  ];

  const compareFields = (oldField = {}, newField = {}) => {
    let hasChange = false;
    if (oldField?.length !== newField?.length) {
      hasChange = true;
      return hasChange;
    } else {
      for (let i = 0, len = oldField.length; i < len; i++) {
        const {
          crmField: oldCrmField = "",
          sgField: oldSgField = "",
          syncDirection: oldSyncDirection = ""
        } = oldField[i];
        const {
          crmField: newCrmField = "",
          sgField: newSgField = "",
          syncDirection: newSyncDirection = ""
        } = newField[i];
        if (oldCrmField === newCrmField) {
          if (
            oldSgField !== newSgField ||
            oldSyncDirection !== newSyncDirection
          ) {
            hasChange = true;
            return hasChange;
          }
        } else {
          hasChange = true;
          return hasChange;
        }
      }
    }
    return hasChange;
  };

  const checkForChanges = (oldData = {}, newData = {}) => {
    let hasChange = false;
    const oldDataTags = oldData?.tags || [];
    const newDataTags = newData?.tags || [];
    if (
      oldData?.autoSyncEnabled !== newData?.autoSyncEnabled ||
      oldData?.sourceId !== newData?.sourceId ||
      oldData?.updateContact !== newData?.updateContact ||
      JSON.stringify(oldData?.pollingInterval) !==
        JSON.stringify(newData?.pollingInterval) ||
      compareFields(oldData?.fields, newData?.fields) ||
      JSON.stringify(oldDataTags?.sort()) !==
        JSON.stringify(newDataTags?.sort())
    ) {
      hasChange = true;
      return hasChange;
    }
    return hasChange;
  };

  const handleTabNavigation = (id, cbkVal = false) => {
    if (cbkVal) {
      setActiveTab(id);
    } else {
      const cbkValue = getActiveTab(id);
      cbkValue && setActiveTab(id);
    }
    history.push(`#${id}`);
  };

  const confirmationCbk = activeTab => {
    const { config: { lead = {}, contact = {} } = {} } = crmSettings;
    setActiveTab(activeTab);
    setPreviousTab(activeTab);
    confirmationPopupState.setShowPopup(false);
    zohoDataStore.setZohoLeadConfig(lead);
    zohoDataStore.setZohoContactConfig(contact);
    zohoDataStore.setZohoLeadTags([...(lead?.tags || [])]);
    zohoDataStore.setZohoContactTags([...(contact?.tags || [])]);
    handleTabNavigation(activeTab, true);
  };

  const getActiveTab = activeTab => {
    let hasChange = false;
    if (previousTab !== activeTab) {
      const payload = constructZohoConfig();
      const {
        config: { lead: payloadLead = {}, contact: payloadContact = {} } = {}
      } = payload || {};
      const { config: { lead = {}, contact = {} } = {} } = crmSettings || {};

      if (previousTab === "leads") {
        hasChange = checkForChanges(lead, payloadLead);
      } else if (previousTab === "contacts") {
        hasChange = checkForChanges(contact, payloadContact);
      }

      if (hasChange) {
        confirmationPopupState.setPopupValues({
          content:
            "Are you sure you want to switch tab ?<br>  Any changes made will be lost.",
          actionBtnText: "Ok",
          callback: () => {
            confirmationCbk(activeTab);
          }
        });
        confirmationPopupState.setShowPopup(true);
        return false;
      } else {
        setActiveTab(activeTab);
        setPreviousTab(activeTab);
        return true;
      }
    }
  };

  useEffect(() => {
    if (["leads", "contacts"]?.includes(activeTab)) {
      zohoDataStore.getAllZohoFields(activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    const updatedHashValue = hashValue.includes("?")
      ? hashValue?.split("?")?.[0]
      : hashValue;
    if (hashUrlArr?.includes(updatedHashValue)) {
      setActiveTab(updatedHashValue.replace("#", ""));
    }
  }, []);

  return (
    <div className={`crmWrapper ${className}`}>
      <div className="tabFieldsWrapper">
        <TabNavigation
          tabList={tabList}
          handleTabNavigation={handleTabNavigation}
          activeTab={activeTab}
        />
      </div>
      {activeTab === "leads" && (
        <LeadsTab
          lead={Utils.createObjWithoutRef(crmSettings?.config?.lead)}
          id={crmSettings?.id}
        />
      )}
      {activeTab === "contacts" && (
        <ContactsTab
          contact={Utils.createObjWithoutRef(crmSettings?.config?.contact)}
          id={crmSettings?.id}
        />
      )}
      {activeTab === "owners" && (
        <OwnersTab
          crmName="Zoho"
          ownersList={zohoDataStore?.zohoOwnersList}
          setOwnersList={zohoDataStore?.setZohoOwnersList}
          config={zohoDataStore?.zohoOwnerConfig}
          setConfig={zohoDataStore?.setZohoOwnerConfig}
          getAllOwners={zohoDataStore?.getAllOwners}
        />
      )}
      {activeTab === "activity" && <ActivityTab />}
      {activeTab === "logs" && <SyncLogs />}
    </div>
  );
});

export { ZohoTabFields };
export default ZohoTabFields;
